import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef} from 'react';
import { useAuth } from './AuthContext';
import { API_URL } from '../constants/config';
import { io } from 'socket.io-client';

interface PointsContextType {
  points: number;
  addPoints: (points: number, reason?: string) => Promise<{ success: boolean; error?: string }>;
  redeemPoints: (points: number) => Promise<{ success: boolean; error?: string; discountAmount?: number }>;
  pointsToCurrency: (points: number) => number;
  refreshPoints: () => Promise<void>;
  loading: boolean;
  lastRefreshed: Date | null;
  forceRefresh: () => void;
}

const PointsContext = createContext<PointsContextType | undefined>(undefined);

export const usePoints = () => {
  const context = useContext(PointsContext);
  if (!context) throw new Error('usePoints must be used within PointsProvider');
  return context;
};

interface PointsProviderProps {
  children: ReactNode;
}

export const PointsProvider: React.FC<PointsProviderProps> = ({ children }) => {
  const [points, setPoints] = useState(0);
  const [loading, setLoading] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const { user, isAuthenticated } = useAuth();
  const refreshTriggerRef = useRef(0);

  // Get auth token
  const getToken = () => localStorage.getItem('token');

  // Fetch points from backend - wrapped in useCallback to prevent recreation
  const refreshPoints = useCallback(async () => {
    if (!isAuthenticated || !user) {
      console.log('Not authenticated, skipping points fetch');
      return;
    }

    setLoading(true);
    const token = getToken();
    
    try {
      console.log('Fetching points from backend...');
      const response = await fetch(`${API_URL}/points/balance`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('📊 Points from backend:', data.balance);
        setPoints(data.balance || 0);
        setLastRefreshed(new Date());
        localStorage.setItem(`points_backup_${user.id}`, data.balance.toString());
      } else {
        console.error('Failed to fetch points:', response.status);
        const savedPoints = localStorage.getItem(`points_backup_${user.id}`);
        if (savedPoints) setPoints(parseInt(savedPoints));
      }
    } catch (error) {
      console.error('Error fetching points:', error);
      const savedPoints = localStorage.getItem(`points_backup_${user.id}`);
      if (savedPoints) setPoints(parseInt(savedPoints));
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  // Force refresh - can be called from components
  const forceRefresh = useCallback(() => {
    refreshTriggerRef.current += 1;
    refreshPoints();
  }, [refreshPoints]);

  // Fetch points when user logs in - now refreshPoints is stable
  useEffect(() => {
    if (isAuthenticated && user) {
      refreshPoints();
    } else {
      setPoints(0);
    }
  }, [isAuthenticated, user, refreshPoints]); // Added refreshPoints to dependencies

  useEffect(() => {
  if (!isAuthenticated || !user) return;

  const socket = io(API_URL, {
    transports: ['websocket', 'polling'],
    query: { token: localStorage.getItem('token') }
  });

  // Listen for points update events
  socket.on('points_updated', (data) => {
    console.log('📡 Points update received:', data);
    if (data.userId === user.id) {
      setPoints(data.newBalance);
      setLastRefreshed(new Date());
      localStorage.setItem(`points_backup_${user.id}`, data.newBalance.toString());
    }
  });

  return () => {
    socket.disconnect();
  };
}, [isAuthenticated, user]);

  // Add points to backend
  const addPoints = useCallback(async (newPoints: number, reason?: string): Promise<{ success: boolean; error?: string }> => {
    if (!isAuthenticated || !user) {
      return { success: false, error: 'User not authenticated' };
    }

    const token = getToken();
    
    try {
      const response = await fetch(`${API_URL}/points/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          points: newPoints, 
          reason: reason || 'Points earned from purchase' 
        })
      });

      if (response.ok) {
        const data = await response.json();
        setPoints(data.balance);
        localStorage.setItem(`points_backup_${user.id}`, data.balance.toString());
        return { success: true };
      } else {
        const error = await response.json();
        return { success: false, error: error.error || 'Failed to add points' };
      }
    } catch (error) {
      console.error('Error adding points:', error);
      return { success: false, error: 'Network error' };
    }
  }, [isAuthenticated, user]);

  // Redeem points
  const redeemPoints = useCallback(async (redeemAmount: number): Promise<{ success: boolean; error?: string; discountAmount?: number }> => {
    if (!isAuthenticated || !user) {
      return { success: false, error: 'User not authenticated' };
    }

    if (redeemAmount <= 0) {
      return { success: false, error: 'Invalid points amount' };
    }

    if (redeemAmount > points) {
      return { success: false, error: 'Insufficient points' };
    }

    const token = getToken();
    
    try {
      const response = await fetch(`${API_URL}/points/redeem`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ points: redeemAmount })
      });

      if (response.ok) {
        const data = await response.json();
        setPoints(data.newBalance);
        localStorage.setItem(`points_backup_${user.id}`, data.newBalance.toString());
        return { 
          success: true, 
          discountAmount: data.discountAmount 
        };
      } else {
        const error = await response.json();
        return { success: false, error: error.error || 'Failed to redeem points' };
      }
    } catch (error) {
      console.error('Error redeeming points:', error);
      return { success: false, error: 'Network error' };
    }
  }, [isAuthenticated, user, points]);

  const pointsToCurrency = useCallback((pointValue: number) => pointValue * 0.1, []);

  return (
    <PointsContext.Provider value={{ 
      points, 
      addPoints, 
      redeemPoints, 
      pointsToCurrency, 
      refreshPoints, 
      loading,
      lastRefreshed,
      forceRefresh
    }}>
      {children}
    </PointsContext.Provider>
  );
};